(() => {
  if (customElements.get('product-subscription-form-widget')) return;

  class ProductSubscriptionFormWidget extends HTMLElement {
    static REVEAL_DELAY_MS = 1000;

    constructor() {
      super();
      this.onPlanSelectChange = this.onPlanSelectChange.bind(this);
      this.onPlanSelectClick = this.onPlanSelectClick.bind(this);
      this.onAddButtonClick = this.onAddButtonClick.bind(this);
      this.onOnetimeAdd = this.onOnetimeAdd.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onVariantInputChange = this.onVariantInputChange.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.appliedVariantId = null;
      this.isAddingToCart = false;
      this.revealTimeoutId = null;
    }

    connectedCallback() {
      this.productForm = this.closest('product-form');
      this.form = this.closest('form');
      this.variantInput = this.form?.querySelector('.product-variant-id');
      this.sellingPlanInput = this.form?.querySelector('[data-js-selling-plan-input]');
      this.addButton = this.querySelector('[data-js-add-button]');
      this.onetimeLink = this.querySelector('[data-js-onetime-add]');
      this.planSelect = this.querySelector('[data-js-selling-plan-select]');
      this.variantDataEl = this.closest('.psfw_root')?.querySelector('[data-js-variant-data]');

      this.titleEl = this.querySelector('[data-js-subscription-title]');
      this.deliveryEl = this.querySelector('[data-js-delivery]');
      this.compareEl = this.querySelector('[data-js-subscription-compare]');
      this.currentEl = this.querySelector('[data-js-subscription-current]');
      this.saveEl = this.querySelector('[data-js-save-badge]');

      this.variantData = this.parseVariantData();

      if (this.planSelect) {
        this.planSelect.addEventListener('change', this.onPlanSelectChange);
        this.planSelect.addEventListener('click', this.onPlanSelectClick);
      }

      if (this.addButton) {
        this.addButton.addEventListener('click', this.onAddButtonClick);
      }

      if (this.onetimeLink) {
        this.onetimeLink.addEventListener('click', this.onOnetimeAdd);
      }

      if (this.form) {
        this.form.addEventListener('submit', this.onFormSubmit, { capture: true });
      }

      if (this.variantInput) {
        this.variantInput.addEventListener('change', this.onVariantInputChange);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      document.addEventListener('shopify:section:load', this.onSectionLoad);

      const variantId = this.getCurrentVariantId();
      if (variantId) {
        this.applyVariant(variantId, { updatePickerPrices: true });
      }

      this.scheduleReveal();
    }

    disconnectedCallback() {
      if (this.planSelect) {
        this.planSelect.removeEventListener('change', this.onPlanSelectChange);
        this.planSelect.removeEventListener('click', this.onPlanSelectClick);
      }

      if (this.addButton) {
        this.addButton.removeEventListener('click', this.onAddButtonClick);
      }

      if (this.onetimeLink) {
        this.onetimeLink.removeEventListener('click', this.onOnetimeAdd);
      }

      if (this.form) {
        this.form.removeEventListener('submit', this.onFormSubmit, { capture: true });
      }

      if (this.variantInput) {
        this.variantInput.removeEventListener('change', this.onVariantInputChange);
      }

      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }

      document.removeEventListener('shopify:section:load', this.onSectionLoad);
      this.clearRevealTimeout();
    }

    clearRevealTimeout() {
      if (this.revealTimeoutId === null) return;
      window.clearTimeout(this.revealTimeoutId);
      this.revealTimeoutId = null;
    }

    scheduleReveal() {
      const root = this.closest('.psfw_root');
      root?.classList.remove('is-initialized');
      this.clearRevealTimeout();

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        this.revealWidget();
        return;
      }

      this.revealTimeoutId = window.setTimeout(() => {
        this.revealTimeoutId = null;
        this.revealWidget();
      }, ProductSubscriptionFormWidget.REVEAL_DELAY_MS);
    }

    revealWidget() {
      const root = this.closest('.psfw_root');

      requestAnimationFrame(() => {
        root?.classList.add('is-initialized');
      });
    }

    onSectionLoad(event) {
      if (!event.target.contains(this)) return;
      this.variantData = this.parseVariantData();
      this.appliedVariantId = null;
      this.applyVariant(this.getCurrentVariantId(), { updatePickerPrices: true });
      this.scheduleReveal();
    }

    parseVariantData() {
      if (!this.variantDataEl) return {};

      try {
        return JSON.parse(this.variantDataEl.textContent);
      } catch {
        return {};
      }
    }

    getCurrentVariantId() {
      return this.variantInput?.value || '';
    }

    getProductInfoRoot() {
      const sectionId = this.dataset.sectionId;
      return (
        document.getElementById(`ProductInfo-${sectionId}`) ||
        this.closest('.product__info-container') ||
        document.querySelector('.product__info-wrapper')
      );
    }

    onVariantInputChange() {
      const variantId = this.getCurrentVariantId();
      if (!variantId) return;
      this.applyVariant(variantId);
    }

    onVariantChange(event) {
      const sectionId = this.dataset.sectionId;
      if (!sectionId || event.data.sectionId !== sectionId) return;
      if (event.data.variant?.id) {
        this.applyVariant(String(event.data.variant.id));
      }
    }

    onPlanSelectClick(event) {
      event.stopPropagation();
    }

    onPlanSelectChange() {
      this.syncPlanFromSelect();
    }

    onFormSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      this.onAddButtonClick(event);
    }

    onAddButtonClick(event) {
      event.preventDefault();

      if (
        this.isAddingToCart ||
        this.addButton?.disabled ||
        this.addButton?.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      this.syncSubscriptionSellingPlan();
      this.performCartAdd({ includeSellingPlan: true });
    }

    onOnetimeAdd(event) {
      event.preventDefault();
      event.stopPropagation();

      if (
        this.isAddingToCart ||
        this.onetimeLink?.disabled ||
        this.onetimeLink?.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      this.performCartAdd({ includeSellingPlan: false });
    }

    syncSubscriptionSellingPlan() {
      if (!this.sellingPlanInput) return;

      const variantData = this.variantData[this.getCurrentVariantId()];
      const planId = this.planSelect?.value || variantData?.sellingPlanId || '';

      if (!planId) {
        this.sellingPlanInput.value = '';
        this.sellingPlanInput.disabled = true;
        return;
      }

      this.sellingPlanInput.disabled = false;
      this.sellingPlanInput.value = planId;
    }

    syncPlanFromSelect(options = {}) {
      const updatePickerPrices = options.updatePickerPrices !== false;

      if (this.planSelect) {
        const selectedOption = this.planSelect.selectedOptions[0];
        if (selectedOption) {
          this.updateSubscriptionDisplay({
            deliveryLabel: selectedOption.dataset.deliveryLabel || '',
            compareFormatted: selectedOption.dataset.compareFormatted || '',
            currentFormatted: selectedOption.dataset.currentFormatted || '',
            saveLabel: selectedOption.dataset.saveLabel || '',
            savePercent: selectedOption.dataset.savePercent || '',
          });
        }
      } else {
        const variantData = this.variantData[this.getCurrentVariantId()];
        if (variantData) {
          this.updateSubscriptionDisplay({
            deliveryLabel: variantData.deliveryLabel || '',
            compareFormatted: variantData.subscriptionCompareFormatted || '',
            currentFormatted: variantData.subscriptionPriceFormatted || '',
            saveLabel: this.buildSaveLabel(variantData.subscriptionSavePercent),
            savePercent: variantData.subscriptionSavePercent || '',
          });
        }
      }

      this.syncSubscriptionSellingPlan();

      if (updatePickerPrices) {
        this.updateVariantPickerPrices();
      }
    }

    updateSubscriptionDisplay(data) {
      if (this.deliveryEl) {
        const hidden = !data.deliveryLabel;
        if (this.deliveryEl.textContent !== data.deliveryLabel) {
          this.deliveryEl.textContent = data.deliveryLabel;
        }
        if (this.deliveryEl.hidden !== hidden) {
          this.deliveryEl.hidden = hidden;
        }
      }

      if (this.compareEl) {
        const hidden = !data.compareFormatted;
        if (this.compareEl.textContent !== data.compareFormatted) {
          this.compareEl.textContent = data.compareFormatted;
        }
        if (this.compareEl.hidden !== hidden) {
          this.compareEl.hidden = hidden;
        }
      }

      if (this.currentEl && this.currentEl.textContent !== data.currentFormatted) {
        this.currentEl.textContent = data.currentFormatted;
      }

      if (this.saveEl) {
        const hidden = !data.saveLabel;
        if (this.saveEl.textContent !== data.saveLabel) {
          this.saveEl.textContent = data.saveLabel;
        }
        if (this.saveEl.hidden !== hidden) {
          this.saveEl.hidden = hidden;
        }
      }

      if (this.titleEl) {
        const saveTemplate = this.dataset.subscribeSaveTemplate || '';
        const defaultTemplate = this.dataset.subscribeTemplate || this.titleEl.textContent;
        const nextTitle =
          data.savePercent && saveTemplate.includes('[percent]')
            ? saveTemplate.replace('[percent]', data.savePercent)
            : defaultTemplate;

        if (this.titleEl.textContent !== nextTitle) {
          this.titleEl.textContent = nextTitle;
        }
      }
    }

    updateOnetimeLink(variantId) {
      if (!this.onetimeLink) return;

      const data = this.variantData[variantId];
      if (!data?.onetimePriceFormatted) return;

      const template = this.dataset.onetimeTemplate || 'One-time purchase - [price]';
      const label = template.replace('[price]', data.onetimePriceFormatted);

      if (this.onetimeLink.textContent !== label) {
        this.onetimeLink.textContent = label;
      }

      if (data.soldOut) {
        this.onetimeLink.setAttribute('disabled', 'disabled');
        this.onetimeLink.setAttribute('aria-disabled', 'true');
      } else {
        this.onetimeLink.removeAttribute('disabled');
        this.onetimeLink.removeAttribute('aria-disabled');
      }
    }

    getVariantSubscriptionPricing(variantId) {
      const data = this.variantData[variantId];
      if (!data?.hasSubscription) return null;

      if (variantId === this.getCurrentVariantId() && this.planSelect?.selectedOptions[0]) {
        const option = this.planSelect.selectedOptions[0];
        return {
          currentFormatted: option.dataset.currentFormatted || data.subscriptionPriceFormatted,
          compareFormatted: option.dataset.compareFormatted || data.subscriptionCompareFormatted,
          savePercent: option.dataset.savePercent || data.subscriptionSavePercent,
        };
      }

      const firstPlan = data.sellingPlans?.[0];
      if (firstPlan) {
        return {
          currentFormatted: firstPlan.currentFormatted || data.subscriptionPriceFormatted,
          compareFormatted: firstPlan.compareFormatted || data.subscriptionCompareFormatted,
          savePercent: firstPlan.savePercent || data.subscriptionSavePercent,
        };
      }

      return {
        currentFormatted: data.subscriptionPriceFormatted,
        compareFormatted: data.subscriptionCompareFormatted,
        savePercent: data.subscriptionSavePercent,
      };
    }

    updateVariantPickerPrices() {
      const root = this.getProductInfoRoot();
      if (!root) return;

      const saveLabelPrefix = (this.dataset.variantSaveLabel || 'Save').trim();

      root.querySelectorAll('.product_variant_label[data-variant-id]').forEach((label) => {
        const variantId = label.dataset.variantId;
        const data = this.variantData[variantId];
        if (!data?.hasSubscription) return;

        const priceEl = label.querySelector('.variant_bundle_pricebox .variant__price');
        const compareEl = label.querySelector('.variant_bundle_pricebox .variant__cprice');
        const badgeEl = label.querySelector('.variant__savebadge');
        if (!priceEl) return;

        const pricing = this.getVariantSubscriptionPricing(variantId);
        if (!pricing) return;

        if (pricing.currentFormatted) {
          priceEl.textContent = pricing.currentFormatted;
        }

        if (compareEl) {
          if (pricing.compareFormatted) {
            compareEl.textContent = pricing.compareFormatted;
            compareEl.style.display = '';
          } else if (compareEl.dataset.hasCompare === 'false' && data.onetimePriceFormatted) {
            compareEl.textContent = data.onetimeCompareFormatted || data.onetimePriceFormatted;
            compareEl.style.display = '';
          }
        }

        if (badgeEl) {
          if (pricing.savePercent) {
            badgeEl.textContent = `${saveLabelPrefix} ${pricing.savePercent}%`;
            badgeEl.style.display = '';
          } else {
            badgeEl.style.display = 'none';
          }
        }
      });
    }

    applyVariant(variantId, options = {}) {
      if (!variantId || !this.variantData[variantId]) return;
      if (variantId === this.appliedVariantId) return;

      const data = this.variantData[variantId];
      this.appliedVariantId = variantId;

      if (this.variantInput) {
        this.variantInput.value = variantId;
        this.variantInput.disabled = data.soldOut;
      }

      if (this.addButton) {
        const addToCartLabel = this.addButton.dataset.addToCartLabel || 'Add to cart';
        const label = data.soldOut ? window.variantStrings?.soldOut || 'Sold out' : addToCartLabel;
        const labelEl = this.addButton.querySelector('span');

        if (data.soldOut) {
          this.addButton.setAttribute('disabled', 'disabled');
          this.addButton.setAttribute('aria-disabled', 'true');
        } else {
          this.addButton.removeAttribute('disabled');
          this.addButton.removeAttribute('aria-disabled');
        }

        if (labelEl && labelEl.textContent !== label) {
          labelEl.textContent = label;
        }
      }

      if (this.planSelect) {
        this.rebuildPlanSelect(data);
      }

      this.syncPlanFromSelect({ updatePickerPrices: options.updatePickerPrices !== false });
      this.updateOnetimeLink(variantId);
    }

    rebuildPlanSelect(data) {
      if (!this.planSelect || !Array.isArray(data.sellingPlans)) return;

      const plansSignature = data.sellingPlans
        .map(
          (plan) =>
            `${plan.id}:${plan.deliveryLabel}:${plan.compareFormatted}:${plan.currentFormatted}:${plan.savePercent}`
        )
        .join('|');

      if (this.planSelect.dataset.plansSignature === plansSignature) {
        return;
      }

      this.planSelect.dataset.plansSignature = plansSignature;
      const currentValue = this.planSelect.value;
      this.planSelect.innerHTML = '';

      data.sellingPlans.forEach((plan, index) => {
        const option = document.createElement('option');
        option.value = plan.id;
        option.textContent = plan.deliveryLabel;
        option.dataset.deliveryLabel = plan.deliveryLabel || '';
        option.dataset.compareFormatted = plan.compareFormatted || '';
        option.dataset.currentFormatted = plan.currentFormatted || '';
        option.dataset.saveLabel = plan.saveLabel || '';
        option.dataset.savePercent = plan.savePercent || '';
        if (index === 0 || String(plan.id) === currentValue) {
          option.selected = String(plan.id) === currentValue || (index === 0 && !currentValue);
        }
        this.planSelect.appendChild(option);
      });
    }

    buildSaveLabel(savePercent) {
      const template = this.dataset.saveBadgeTemplate || 'Save [percent]%';
      if (!savePercent) return '';
      return template.replace('[percent]', savePercent);
    }

    getCartElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    getCartAddUrl() {
      if (typeof routes !== 'undefined' && routes.cart_add_url) {
        return routes.cart_add_url;
      }

      return `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    }

    buildCartAddFormData({ includeSellingPlan }) {
      const variantId = this.getCurrentVariantId();
      if (!variantId) return null;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      if (includeSellingPlan) {
        const planId = this.planSelect?.value || this.variantData[variantId]?.sellingPlanId || '';
        if (planId) {
          formData.append('selling_plan', planId);
        }
      }

      const cart = this.getCartElement();
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      return formData;
    }

    setAddButtonLoading(isLoading) {
      if (!this.addButton) return;

      this.addButton.classList.toggle('loading', isLoading);
      const spinner = this.addButton.querySelector('.loading__spinner');
      if (spinner) {
        spinner.classList.toggle('hidden', !isLoading);
      }

      if (isLoading) {
        this.addButton.setAttribute('aria-disabled', 'true');
      } else if (!this.variantData[this.getCurrentVariantId()]?.soldOut) {
        this.addButton.removeAttribute('aria-disabled');
      }
    }

    setOnetimeLinkLoading(isLoading) {
      if (!this.onetimeLink) return;
      this.onetimeLink.classList.toggle('loading', isLoading);
      if (isLoading) {
        this.onetimeLink.setAttribute('aria-disabled', 'true');
      } else if (!this.variantData[this.getCurrentVariantId()]?.soldOut) {
        this.onetimeLink.removeAttribute('aria-disabled');
      }
    }

    async performCartAdd({ includeSellingPlan }) {
      if (this.isAddingToCart) return;
      const formData = this.buildCartAddFormData({ includeSellingPlan });
      const variantId = formData?.get('id');
      if (!variantId) return;

      this.isAddingToCart = true;

      if (includeSellingPlan) {
        this.setAddButtonLoading(true);
      } else {
        this.setOnetimeLinkLoading(true);
      }

      const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST' };
      config.headers = config.headers || {};
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      try {
        const response = await fetch(this.getCartAddUrl(), config);
        const data = await response.json();

        if (data.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-subscription-form-widget',
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });
          }
          return;
        }

        const cart = this.getCartElement();
        if (!cart || typeof cart.renderContents !== 'function') {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-subscription-form-widget',
            productVariantId: variantId,
            cartData: data,
          });
        }

        cart.renderContents(data);

        if (cart.classList.contains('is-empty')) {
          cart.classList.remove('is-empty');
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isAddingToCart = false;

        if (includeSellingPlan) {
          this.setAddButtonLoading(false);
        } else {
          this.setOnetimeLinkLoading(false);
        }
      }
    }
  }

  customElements.define('product-subscription-form-widget', ProductSubscriptionFormWidget);
})();
